import type { PresetName } from './presets.js';

/**
 * Global burst bus.
 *
 * Any code can fire a one-shot particle burst at a screen position without
 * holding an engine reference: `burst({ preset: 'bloodBurst', x, y })`. A single
 * full-screen `<BurstLayer>` (mounted once, high in the tree) subscribes to the
 * bus and renders every burst. This keeps burst effects decoupled from the
 * layout — a kill animation deep in a component can splatter blood on the whole
 * screen without threading a canvas ref through props.
 *
 * Coordinates are normalized 0..1 across the viewport. Under reduced motion a
 * burst is a no-op (the vestibular-safe path is ShakeContainer's `flash()`,
 * which callers trigger separately — the bus deliberately does not flash).
 */

export interface BurstRequest {
  preset: PresetName;
  /** Origin X, 0..1 across the viewport. */
  x: number;
  /** Origin Y, 0..1 across the viewport. */
  y: number;
  /** Override the preset's default particle count. */
  count?: number;
}

type Listener = (req: BurstRequest) => void;

const listeners = new Set<Listener>();

/** Fire a burst. Delivered to the mounted BurstLayer (no-op if none mounted). */
export function burst(req: BurstRequest): void {
  for (const l of listeners) l(req);
}

/** BurstLayer subscribes here; returns an unsubscribe. Internal. */
export function subscribeBurst(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Convenience: convert absolute client px to normalized viewport coords. */
export function clientToNormalized(clientX: number, clientY: number): { x: number; y: number } {
  return {
    x: clientX / window.innerWidth,
    y: clientY / window.innerHeight,
  };
}
